import { useState, useEffect } from "react";
import styles from "./DesktopPaymentAndFees.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import PaymentFilterGroup from "./components/PaymentFilterGroup";
import PaymentChevronDown from "@Icons/PaymentChevronDown";
import OfferChevronRight from "@Icons/OfferChevronRight";
import MenuDropdown from "@Components/MenuDropdown";
import RadioButton from "@Components/Radio";
import PropertyDetailsModal from "../PropertyDetailsModal";
import getPaymentPolicyIcon from "./utils/getPaymentPolicyIcon";
import replaceAmberDomainInLinks from "@Utils/clientUtils/dangerouslySetInnerHtmlUtility/replaceAmberDomainInLinks";
import CalendarClockIcon from "@Icons/CalendarClockIcon";
import useTranslationHook from "hooks/useTranslationHook";
import ChinaPaymentLogos from "@Components/ChinaPaymentLogos";
import config from "config/index";
import FeesAccordionSection from "./components/FeesAccordionSection";
import { FeesSectionData } from "./types";

interface DesktopPaymentAndFeesProps {
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
  selectedDuration: string;
  setSelectedDuration: (duration: string) => void;
  setSelectedYear: (year: string) => void;
  durations: string[];
  years: string[];
  displayPlans: any[];
  allCount: number;
  withGuarantorCount: number;
  withoutGuarantorCount: number;
  paymentPolicies: any[]; // Changed from individual policies to array
  hasInstallmentPlans?: boolean;
  requiredFees?: FeesSectionData | null;
  addOnFees?: FeesSectionData | null;
  baseCurrency?: string;
  pricingDuration?: string;
  feesConditionalNotes?: string[] | null;
  isYearOpen?: boolean;
  setIsYearOpen?: (open: boolean) => void;
  isDurationOpen?: boolean;
  setIsDurationOpen?: (open: boolean) => void;
  forceCloseYear?: boolean;
  setForceCloseYear?: (close: boolean) => void;
  forceCloseDuration?: boolean;
  setForceCloseDuration?: (close: boolean) => void;
}

export default ({
  selectedTab,
  setSelectedTab,
  selectedDuration,
  setSelectedDuration,
  setSelectedYear,
  durations,
  years,
  displayPlans,
  allCount,
  withGuarantorCount,
  withoutGuarantorCount,
  paymentPolicies,
  hasInstallmentPlans = false,
  requiredFees = null,
  addOnFees = null,
  baseCurrency,
  pricingDuration,
  feesConditionalNotes = null,
  isYearOpen = false,
  setIsYearOpen,
  isDurationOpen = false,
  setIsDurationOpen,
  forceCloseYear = false,
  setForceCloseYear,
  forceCloseDuration = false,
  setForceCloseDuration,
}: DesktopPaymentAndFeesProps) => {
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);
  const [durationClose, setDurationClose] = useState(false);
  const [activeFilters, setActiveFilters] = useState({ duration: false });
  const [openModal, setOpenModal] = useState<string | null>(null);
  const { t } = useTranslationHook();

  // Handle force close from parent
  useEffect(() => {
    if (forceCloseDuration) {
      setActiveFilters((prev) => ({ ...prev, duration: false }));
      setDurationClose(true);
      setTimeout(() => setDurationClose(false), 100);
    }
  }, [forceCloseDuration]);

  const handlePlanToggle = (index: number) => {
    setExpandedPlan(expandedPlan === index ? null : index);
  };

  const openPolicyModal = (sectionKey: string) => {
    setOpenModal(sectionKey);
  };

  const closePolicyModal = () => {
    setOpenModal(null);
  };

  const handleDurationSelect = (duration: string) => {
    setSelectedDuration(duration);
    if (setIsDurationOpen) {
      setIsDurationOpen(false);
    }
    setDurationClose(true);
    setActiveFilters((prev) => ({ ...prev, duration: false }));
    setTimeout(() => setDurationClose(false), 100);
  };

  const resetDuration = () => {
    setSelectedDuration("All");
    if (setIsDurationOpen) {
      setIsDurationOpen(false);
    }
    setDurationClose(true);
    setActiveFilters((prev) => ({ ...prev, duration: false }));
    setTimeout(() => setDurationClose(false), 100);
  };

  const onClickDuration = () => {
    const willOpen = !isDurationOpen;
    if (setIsDurationOpen) {
      setIsDurationOpen(willOpen);
    }
    setActiveFilters((prev) => ({ ...prev, duration: willOpen }));

    // Close year filter when duration opens
    if (willOpen && setIsYearOpen) {
      setIsYearOpen(false);
      if (setForceCloseYear) {
        setForceCloseYear(true);
        setTimeout(() => setForceCloseYear(false), 100);
      }
    }
  };

  const durationMenu = (
    <div className={styles.dropdownMenu} role="menu">
      <div className={styles.heading_filter}>
        <div className={styles.heading}>Duration</div>
        <div
          className={formatClassNames(styles.reset, selectedDuration === "All" && styles.inactive)}
          onClick={() => {
            if (selectedDuration !== "All") resetDuration();
          }}
          role="none"
        >
          Reset
        </div>
      </div>
      <div className={styles.divider} />
      <div className={styles.listItems}>
        {durations.map((duration) => (
          <div
            className={styles.item}
            key={`duration-${duration}`}
            onClick={() => handleDurationSelect(duration)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleDurationSelect(duration);
              }
            }}
            role="none"
          >
            <RadioButton isSelected={duration === selectedDuration} />
            <span className={styles.filterDropdownText}>{duration}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const getGuarantorType = (plan: any) => {
    const type = plan.meta?.guarantor_required;
    switch (type) {
      case "local_without_third_party":
        /* "Local guarantors only (third party guarantor service not allowed)" */
        return t("propertypage.paymentandfees.guarantor.localNoThirdParty");
      case "local_with_third_party":
        /* "Local guarantors only (third party guarantor service allowed)" */
        return t("propertypage.paymentandfees.guarantor.localWithThirdParty");
      case "international_local":
        /* "International and local guarantors allowed" */
        return t("propertypage.paymentandfees.guarantor.internationalLocal");
      case "false":
        /* "No guarantor required" */
        return t("propertypage.paymentandfees.guarantor.none");
      case "information_unavailable":
        /* "Information unavailable" */
        return t("propertypage.paymentandfees.guarantor.unavailable");
      default:
        /* "Guarantor information not available" */
        return plan.notes?.note || t("propertypage.paymentandfees.guarantor.notAvailable");
    }
  };

  const getPlanTitle = (plan: any) => {
    if (plan.plan_type === "fixed") {
      return plan.number_of_installments === 0
        ? "1 Instalment Plan"
        : `${plan.number_of_installments} Instalment Plan`;
    }
    return "Installment Plan";
  };

  // Helper function to check if policy should show payment icons
  const shouldShowPaymentIcons = (tag: string) => tag === "mode_of_payment";

  // Helper function to strip HTML tags and get plain text
  const stripHtmlTags = (html: string) => {
    if (typeof document === "undefined") {
      return html.replace(/<[^>]*>?/gm, "");
    }
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const hasStructuredFees = Boolean(
    requiredFees?.categories?.length || addOnFees?.categories?.length,
  );

  // Determine which section is first if no installment plans
  let firstSection: string | null = null;
  if (!hasInstallmentPlans && !hasStructuredFees && paymentPolicies && paymentPolicies.length > 0) {
    firstSection = paymentPolicies[0]?.tag_en ?? paymentPolicies[0].tag;
  }

  return (
    <div className={styles.container} id="payment_and_fees">
      <div className={styles.sectionHeader}>
        <h2 className={styles.headerTitle}>
          {/* "Payment and Fees" */}
          {t("propertypage.paymentandfees.header")}
        </h2>
      </div>

      <div className={formatClassNames(styles.content)}>
        {hasInstallmentPlans && (
          <div className={styles.policyField}>
            <div className={styles.leftColumn}>
              <div className={styles.columnTitle}>
                {/* "Instalment Plans" */}
                <CalendarClockIcon /> {t("propertypage.paymentandfees.instalmentPlans")}
              </div>
            </div>

            <div className={styles.rightColumn}>
              <div className={styles.filterControls}>
                <div className={styles.segmentedControl}>
                  <div className={styles.segmentedControlInner}>
                    <button
                      type="button"
                      className={formatClassNames(
                        styles.segmentButton,
                        selectedTab === "all" && styles.active,
                      )}
                      onClick={() => setSelectedTab("all")}
                    >
                      {t("propertypage.paymentandfees.all")} ({allCount})
                    </button>
                    {withGuarantorCount > 0 && (
                      <button
                        type="button"
                        className={formatClassNames(
                          styles.segmentButton,
                          selectedTab === "with_guarantor" && styles.active,
                        )}
                        onClick={() => setSelectedTab("with_guarantor")}
                      >
                        {t("propertypage.paymentandfees.withguarantor")} ({withGuarantorCount})
                      </button>
                    )}
                    {withoutGuarantorCount > 0 && (
                      <button
                        type="button"
                        className={formatClassNames(
                          styles.segmentButton,
                          selectedTab === "without_guarantor" && styles.active,
                        )}
                        onClick={() => setSelectedTab("without_guarantor")}
                      >
                        {t("propertypage.paymentandfees.withoutguarantor")} ({withoutGuarantorCount}
                        )
                      </button>
                    )}
                  </div>
                </div>

                <div className={styles.dropdownFilters}>
                  <PaymentFilterGroup
                    filtersData={{ duration: durations, year: years }}
                    setDuration={setSelectedDuration}
                    setYear={setSelectedYear}
                    isYearOpen={isYearOpen}
                    setIsYearOpen={setIsYearOpen}
                    setIsDurationOpen={setIsDurationOpen}
                    forceCloseYear={forceCloseYear}
                    setForceCloseDuration={setForceCloseDuration}
                  />

                  {durations.length > 0 && (
                    <MenuDropdown
                      target={
                        <div
                          className={formatClassNames(
                            styles.filterPill,
                            styles.durationPill,
                            selectedDuration !== "All" && styles.selected,
                            activeFilters?.duration && styles.active,
                          )}
                          onClick={onClickDuration}
                          role="none"
                        >
                          {selectedDuration !== "All"
                            ? selectedDuration
                            : /* "Duration" */
                              t("propertypage.paymentandfees.filters.duration")}
                          <div
                            className={formatClassNames(
                              styles.pillIcon,
                              selectedDuration !== "All" && styles.selected,
                              activeFilters?.duration && styles.active,
                            )}
                          >
                            <PaymentChevronDown
                              width={16}
                              height={16}
                              className={isDurationOpen ? styles.open : ""}
                            />
                          </div>
                        </div>
                      }
                      menu={durationMenu}
                      menuContainerClassName={styles.dropdownMenuContainer}
                      handleOnClose={() => {
                        setActiveFilters((prev) => ({ ...prev, duration: false }));
                        if (setIsDurationOpen) {
                          setIsDurationOpen(false);
                        }
                      }}
                      forceClose={durationClose || forceCloseDuration}
                    />
                  )}
                </div>
              </div>

              <div className={styles.plansList}>
                {displayPlans.map((plan: any, index: number) => (
                  <div key={index} className={styles.planCard}>
                    <div
                      className={styles.planHeader}
                      onClick={() => handlePlanToggle(index)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handlePlanToggle(index);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className={styles.planInfo}>
                        <div className={styles.planTitle}>{getPlanTitle(plan)}</div>
                        <div className={styles.planSubtitle}>
                          {plan.duration} {plan.duration_unit} • {getGuarantorType(plan)}
                        </div>
                      </div>
                      <div
                        className={formatClassNames(
                          styles.chevron,
                          expandedPlan === index && styles.expanded,
                        )}
                      >
                        <PaymentChevronDown width={16} height={16} />
                      </div>
                    </div>

                    {expandedPlan === index &&
                      plan.plan_installments &&
                      plan.plan_installments.length > 0 && (
                        <div className={styles.planDetails}>
                          {plan.plan_installments.map((installment: any, idx: number) => (
                            <div key={idx} className={styles.installmentRow}>
                              <div className={styles.installmentColumn}>
                                <div className={styles.label}>Rent</div>
                                <div className={styles.value}>
                                  {/* "Instalment {idx + 1}" */}
                                  {t("propertypage.paymentandfees.table.instalment", {
                                    idx: String(idx + 1),
                                  })}
                                </div>
                              </div>
                              <div className={styles.installmentColumn}>
                                <div className={styles.label}>Pay Before</div>
                                <div className={styles.value}>
                                  {new Date(installment.due_date).toLocaleDateString("en-GB", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </div>
                              </div>
                              <div className={styles.installmentColumn}>
                                <div className={styles.label}>Amount Due</div>
                                <div className={styles.value}>
                                  {installment.installment_amount}%
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                ))}

                <div className={styles.noteBox}>
                  <span className={styles.noteText}>
                    {/* "Note: The displayed amount is a percentage of the total for illustration purposes. Actual amounts may vary slightly." */}
                    <strong>{t("propertypage.paymentandfees.note.label")}:</strong>{" "}
                    {t("propertypage.paymentandfees.note.percentageDisclaimer")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {requiredFees?.categories?.length ? (
          <FeesAccordionSection
            data={requiredFees}
            isDesktop
            baseCurrency={baseCurrency}
            pricingDuration={pricingDuration}
          />
        ) : null}

        {addOnFees?.categories?.length ? (
          <FeesAccordionSection
            data={addOnFees}
            isDesktop
            baseCurrency={baseCurrency}
            pricingDuration={pricingDuration}
          />
        ) : null}

        {feesConditionalNotes?.length ? (
          <div className={styles.noteBox}>
            {feesConditionalNotes.map((note) => (
              <span key={note} className={styles.noteText}>
                {note}
              </span>
            ))}
          </div>
        ) : null}

        {paymentPolicies &&
          paymentPolicies.length > 0 &&
          paymentPolicies.map((policy: any, index: number) => (
            <div
              key={`${policy.tag}-${index}`}
              className={
                !hasInstallmentPlans &&
                !hasStructuredFees &&
                firstSection === (policy?.tag_en ?? policy.tag)
                  ? `${styles.infoRow} ${styles.noTopBorder}`
                  : styles.infoRow
              }
            >
              <div className={styles.leftColumn}>
                <div className={styles.iconTitle}>
                  <span className={styles.icon}>
                    {getPaymentPolicyIcon(policy?.tag_en ?? policy.tag)}
                  </span>
                  <h3 className={styles.iconTitleText}>{policy.display_name || policy.tag}</h3>
                </div>
              </div>
              <div className={styles.rightColumn}>
                <div
                  className={formatClassNames(
                    styles.infoContent,
                    config.IS_CHINA_APP && styles.infoContentChina,
                  )}
                >
                  {policy.value && <p className={styles.infoText}>{stripHtmlTags(policy.value)}</p>}
                  {shouldShowPaymentIcons(policy?.tag_en ?? policy.tag) &&
                    (config.IS_CHINA_APP ? (
                      <ChinaPaymentLogos dataTestIdPrefix="pdp-payment" />
                    ) : (
                      <div className={styles.paymentIcons}>
                        <div className={styles.paymentIcon}>
                          <svg width="40" height="24" viewBox="0 0 40 24" fill="none">
                            <rect width="40" height="24" rx="4" fill="#003087" />
                            <text
                              x="50%"
                              y="50%"
                              dominantBaseline="middle"
                              textAnchor="middle"
                              fill="white"
                              fontSize="10"
                              fontWeight="bold"
                            >
                              PayPal
                            </text>
                          </svg>
                        </div>
                        <div className={styles.paymentIcon}>
                          <svg width="40" height="24" viewBox="0 0 40 24" fill="none">
                            <rect width="40" height="24" rx="4" fill="#1A1F71" />
                            <text
                              x="50%"
                              y="50%"
                              dominantBaseline="middle"
                              textAnchor="middle"
                              fill="white"
                              fontSize="10"
                              fontWeight="bold"
                            >
                              VISA
                            </text>
                          </svg>
                        </div>
                        <div className={styles.paymentIcon}>
                          <svg width="40" height="24" viewBox="0 0 40 24" fill="none">
                            <rect width="40" height="24" rx="4" fill="#EB001B" />
                            <circle cx="15" cy="12" r="6" fill="#FF5F00" />
                            <circle cx="25" cy="12" r="6" fill="#F79E1B" />
                          </svg>
                        </div>
                        <div className={styles.paymentIcon}>
                          <svg width="40" height="24" viewBox="0 0 40 24" fill="none">
                            <rect width="40" height="24" rx="4" fill="#016FD0" />
                            <text
                              x="50%"
                              y="50%"
                              dominantBaseline="middle"
                              textAnchor="middle"
                              fill="white"
                              fontSize="8"
                              fontWeight="bold"
                            >
                              AMEX
                            </text>
                          </svg>
                        </div>
                      </div>
                    ))}
                  {policy.value && (
                    <button
                      type="button"
                      className={styles.viewMoreButton}
                      onClick={() => openPolicyModal(policy.tag)}
                    >
                      {t("View more")}
                      <span className={styles.arrow}>
                        <OfferChevronRight width={16} height={16} color="#111928" />
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* Policy Modals - Dynamic */}
      {paymentPolicies &&
        paymentPolicies.map(
          (policy: any) =>
            openModal === policy.tag &&
            policy.value && (
              <PropertyDetailsModal
                key={policy.tag}
                header={policy.display_name || policy.tag}
                actionButtonText="Got it"
                hidePropertyModal={closePolicyModal}
                isDesktop
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: replaceAmberDomainInLinks(policy.value),
                  }}
                />
              </PropertyDetailsModal>
            ),
        )}
    </div>
  );
};
